import { Seo } from "@/components/Seo";
import { Reveal, PillButton, Hairline, StatusBadge } from "@/components/primitives";
import { StrategicAssetsNav } from "@/components/StrategicAssetsNav";
import { TAX_OPTIMIZATION_URL } from "@/lib/assets";

const BADGES = ["Read-Only Access", "Encrypted & Masked", "Evidence-Backed", "Tax-Year-Specific Law"];

const STEPS = [
  { n: "01", t: "Learn", d: "Understand how a complete-position tax analysis surfaces opportunities others overlook." },
  { n: "02", t: "Explore", d: "Review competing treatments and the strongest legally supportable outcomes for a given position." },
  { n: "03", t: "Begin", d: "Enter the secure Tax Optimization application to start a confidential analysis." },
];

export default function SATaxOptimization() {
  return (
    <div className="glow-navy min-h-screen">
      <Seo title="Tax Optimization — Strategic Assets" description="Tax Optimization — a current tax-intelligence and optimization technology within Strategic Assets Intelligence. Analyzes a complete tax position to surface the strongest legally supportable outcome. Technology, not tax-advisory, CPA, or legal services." />
      <StrategicAssetsNav />

      <section className="pt-10 md:pt-16 pb-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <StatusBadge status="Current platform capability" className="mb-5" />
            <div className="flex items-center justify-center gap-4">
              <span className="h-px w-14 bg-[var(--gold)]" />
              <span className="font-display uppercase tracking-luxe text-xs md:text-sm text-[var(--bone)]">Tax Optimization</span>
              <span className="h-px w-14 bg-[var(--gold)]" />
            </div>
            <p className="mt-4 font-display uppercase tracking-luxe text-[10px] md:text-xs text-[var(--gold)]/80">A capability of Strategic Assets Intelligence · A Jennae Michelle Holdings company</p>
          </Reveal>

          <Reveal delay={0.12} className="mt-12">
            <h1 className="font-serif2 text-4xl md:text-6xl text-[var(--bone)] leading-tight">Find every lawful advantage.</h1>
            <p className="mt-6 max-w-2xl mx-auto text-lg md:text-xl text-[var(--bone)]/85 leading-relaxed">
              Uses the separate, authoritative tax-intelligence system to analyze a lawful tax position, obligations, alternatives, and planning considerations across a complete filing picture. JMH and this website do not independently recalculate tax; the analysis runs in the dedicated application.
            </p>
          </Reveal>

          <Reveal delay={0.2} className="mt-10 flex flex-wrap items-center justify-center gap-5">
            <PillButton href={TAX_OPTIMIZATION_URL} external testid="tax-begin">Begin Tax Optimization</PillButton>
            <PillButton href={`${TAX_OPTIMIZATION_URL}/analysis`} external testid="tax-view-results">View Result Comparison</PillButton>
          </Reveal>

          <Reveal delay={0.28} className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {BADGES.map((b) => (
              <span key={b} className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)]">{b}</span>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <Hairline className="opacity-70 mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="corner-frame px-7 py-10 h-full">
                  <div className="font-display gold-text text-3xl">{s.n}</div>
                  <div className="mt-3 font-display uppercase tracking-wide2 text-sm text-[var(--gold-lite)]">{s.t}</div>
                  <p className="mt-4 text-[var(--bone)]/85 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-14 text-center">
            <PillButton href={TAX_OPTIMIZATION_URL} external testid="tax-begin-2">Begin Tax Optimization</PillButton>
            <p className="mt-4 text-[11px] text-[var(--muted)]">The secure Tax Optimization application opens at tax-optimization.jennaemichelle.com.</p>
          </Reveal>
        </div>
      </section>

      <div className="pb-20 text-center px-6">
        <p className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)] leading-loose max-w-2xl mx-auto">
          Tax Optimization is a technology platform and tax-intelligence tool. It is not tax-advisory, tax-return preparation, CPA, or legal services, and it does not guarantee tax savings or any particular outcome.
        </p>
      </div>
    </div>
  );
}
